"use client";

import { useState, useCallback, useEffect } from "react";
import Modal from "@/app/components/common/Modal";
import ConfirmDialog from "@/app/components/common/ConfirmDialog";
import { useRouter } from "next/navigation";
import { useAuth } from "@/app/lib/hooks/useAuth";
import { useProviders } from "@/app/lib/hooks/useProviders";
import { FALLBACK_MODES } from "@/app/lib/constants";
import { resolveModeLabel } from "@/app/lib/modes";
import { usePreferences } from "@/app/lib/hooks/usePreferences";
import {
  fetchMemories,
  deleteMemory,
  deleteAllMemories,
  fetchCustomQueries,
  createCustomQuery,
  updateCustomQuery,
  deleteCustomQuery,
  reorderCustomQueries,
  fetchFolders as fetchFoldersApi,
  fetchAllSessions,
  exportForAdmin,
  submitFeedback,
  updateAccountName,
  getAccountName,
} from "@/app/lib/api";
import {
  getAuthState,
  adminDebugEnable,
  adminDebugDisable,
  changePasswordV2,
  rotateRecoveryCodesV2,
} from "@/app/lib/auth";
import RecoveryCodesDisplay from "@/app/components/auth/RecoveryCodesDisplay";
import { formatScheduleSummary } from "@/app/lib/format";
import type {
  MemoryResponse,
  CustomQueryResponse,
  FolderResponse,
  QuerySchedule,
} from "@/app/lib/types";
import { compareFolders } from "@/app/lib/types";
import AdminPanel from "@/app/components/settings/AdminPanel";
import UsagePanel from "@/app/components/settings/UsagePanel";
import { HelpContent } from "@/app/components/help/HelpModal";
import {
  useAdminBadges,
  formatBadgeCount,
  BADGE_PILL_STYLE,
} from "@/app/lib/hooks/useAdminBadges";

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: string | null;
  /** Optional — fired after any custom-query mutation (create / update /
   *  delete / reorder) so the parent page can refresh its own
   *  customQueries list immediately instead of waiting for the 30-second
   *  polling tick. */
  onCustomQueriesChanged?: () => void;
}

type SettingsTab = "account" | "defaults" | "memories" | "instructions" | "queries" | "usage" | "help" | "admin";

function modeOptions(modes: ReturnType<typeof useProviders>["modes"]) {
  const names = modes.length > 0
    ? [...modes].sort((a, b) => a.order - b.order).map((mode) => mode.name)
    : [...FALLBACK_MODES];
  return names.map((value) => ({ value, label: value === "auto" ? "Auto" : resolveModeLabel(value, modes) }));
}

// Defaults include client-side Auto but never Deep Research: that expensive
// mode must be deliberately selected for each chat.
function defaultModeOptions(modes: ReturnType<typeof useProviders>["modes"]) {
  const serverModes = modeOptions(modes).filter((mode) => mode.value !== "auto" && mode.value !== "deep_research");
  return [{ value: "auto", label: "Auto" }, ...serverModes];
}

// Auto-run schedule cadences for custom queries. Matches the server contract
// (`schedule.frequency`).
const SCHEDULE_FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
] as const;

// day_of_week is 0-6 with 0=Monday (server/iOS/web convention).
const SCHEDULE_WEEKDAYS = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

// The browser's IANA timezone — the default a new schedule is stamped with.
const LOCAL_TIMEZONE =
  (typeof Intl !== "undefined" &&
    Intl.DateTimeFormat().resolvedOptions().timeZone) ||
  "America/New_York";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MemoriesTab() {
  const [memories, setMemories] = useState<MemoryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteAll, setShowDeleteAll] = useState(false);

  const loadMemories = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await fetchMemories();
      setMemories(resp.memories);
    } catch (err) {
      console.error("Failed to load memories:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMemories();
  }, [loadMemories]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await deleteMemory(id);
      setMemories((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      console.error("Failed to delete memory:", err);
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleDeleteAll = useCallback(async () => {
    try {
      await deleteAllMemories();
      setMemories([]);
      setShowDeleteAll(false);
    } catch (err) {
      console.error("Failed to delete all memories:", err);
    }
  }, []);

  if (loading) {
    return (
      <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
        Loading memories...
      </div>
    );
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
            {memories.length} {memories.length === 1 ? "memory" : "memories"}
          </span>
          {memories.length > 0 && (
            <button
              className="btn-danger"
              onClick={() => setShowDeleteAll(true)}
              style={{ fontSize: "11px", padding: "4px 10px" }}
            >
              Delete All
            </button>
          )}
        </div>

        {memories.length === 0 ? (
          <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
            No memories yet. Trio learns from your conversations automatically.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "400px", overflowY: "auto" }}>
            {memories.map((memory) => (
              <div
                key={memory.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "10px 12px",
                  background: "var(--bg)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "13px", color: "var(--text-primary)", lineHeight: 1.5 }}>
                    {memory.content}
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "4px" }}>
                    {new Date(memory.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  className="btn-ghost"
                  onClick={() => handleDelete(memory.id)}
                  disabled={deletingId === memory.id}
                  style={{ padding: "4px", flexShrink: 0, color: "var(--text-tertiary)" }}
                  aria-label="Delete memory"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteAll}
        title="Delete All Memories"
        message="Are you sure you want to delete all memories? This cannot be undone."
        confirmLabel="Delete All"
        cancelLabel="Cancel"
        onConfirm={handleDeleteAll}
        onCancel={() => setShowDeleteAll(false)}
        variant="danger"
      />
    </>
  );
}

function QueriesTab({ onCustomQueriesChanged }: { onCustomQueriesChanged?: () => void }) {
  const { modes } = useProviders();
  const modesForPicker = modeOptions(modes);
  const [queries, setQueries] = useState<CustomQueryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Folders
  const [folders, setFolders] = useState<FolderResponse[]>([]);

  // Form state
  const [formName, setFormName] = useState("");
  const [formSystemPrompt, setFormSystemPrompt] = useState("");
  const [formSuggestedPrompt, setFormSuggestedPrompt] = useState("");
  const [formDefaultMode, setFormDefaultMode] = useState("auto");
  const [formFolderId, setFormFolderId] = useState("");
  const [formSchedule, setFormSchedule] = useState<QuerySchedule>({ enabled: false });
  const [saving, setSaving] = useState(false);

  // Sort by user-defined order (ascending), with created_at as tiebreaker.
  // Matches server-side sort + the bottom-toolbar picker's sort, so the
  // Settings list and the picker stay in lock-step.
  const sortQueries = (qs: CustomQueryResponse[]): CustomQueryResponse[] =>
    [...qs].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return (a.created_at || "").localeCompare(b.created_at || "");
    });

  const loadQueries = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchCustomQueries();
      setQueries(sortQueries(data));
    } catch (err) {
      console.error("Failed to load custom queries:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Move query at `idx` by `delta` (-1 = up, +1 = down). Fires the
  // batch-reorder endpoint with the new ID sequence. Optimistic local
  // update; rollback on failure (matches iOS `reorderCustomQueries`).
  const moveQuery = useCallback(async (idx: number, delta: number) => {
    const sorted = sortQueries(queries);
    const target = idx + delta;
    if (target < 0 || target >= sorted.length) return;
    const snapshot = queries;
    const next = [...sorted];
    [next[idx], next[target]] = [next[target], next[idx]];
    // Optimistically rewrite each query's `order` field so the UI reflects
    // the new sequence immediately, before the server round-trip completes.
    const optimistic = next.map((q, i) => ({ ...q, order: i }));
    setQueries(optimistic);
    try {
      const updated = await reorderCustomQueries(next.map((q) => q.id));
      setQueries(sortQueries(updated));
      onCustomQueriesChanged?.();
    } catch (err) {
      console.error("Failed to reorder custom queries:", err);
      setQueries(snapshot);
    }
  }, [queries, onCustomQueriesChanged]);

  useEffect(() => {
    loadQueries();
    fetchFoldersApi().then(f => setFolders(f)).catch(() => {});
  }, [loadQueries]);

  const resetForm = () => {
    setFormName("");
    setFormSystemPrompt("");
    setFormSuggestedPrompt("");
    setFormDefaultMode("auto");
    setFormFolderId("");
    setFormSchedule({ enabled: false });
    setEditingId(null);
    setShowAdd(false);
  };

  const startEdit = (q: CustomQueryResponse) => {
    setEditingId(q.id);
    setShowAdd(false);
    setFormName(q.name);
    setFormSystemPrompt(q.system_prompt || "");
    setFormSuggestedPrompt(q.suggested_prompt || "");
    setFormDefaultMode(q.default_mode ?? "auto");
    setFormFolderId(q.folder_id || "");
    setFormSchedule(q.schedule ? { ...q.schedule } : { enabled: false });
  };

  const startAdd = () => {
    setEditingId(null);
    setShowAdd(true);
    setFormName("");
    setFormSystemPrompt("");
    setFormSuggestedPrompt("");
    setFormDefaultMode("auto");
    setFormFolderId("");
    setFormSchedule({ enabled: false });
  };

  const handleSave = useCallback(async () => {
    if (!formName.trim()) return;
    setSaving(true);
    // A schedule with nothing to send is meaningless — only enable it when
    // there's a suggested prompt. Otherwise force it off before persisting.
    const schedulePayload: QuerySchedule =
      formSchedule.enabled && formSuggestedPrompt.trim()
        ? {
            enabled: true,
            frequency: formSchedule.frequency || "weekly",
            time_of_day: formSchedule.time_of_day || "09:00",
            timezone: formSchedule.timezone || LOCAL_TIMEZONE,
            day_of_week: formSchedule.day_of_week ?? 0,
            day_of_month: formSchedule.day_of_month ?? 1,
          }
        : { enabled: false };
    try {
      if (editingId) {
        const updated = await updateCustomQuery(editingId, {
          name: formName,
          system_prompt: formSystemPrompt || undefined,
          suggested_prompt: formSuggestedPrompt || undefined,
          default_mode: formDefaultMode,
          folder_id: formFolderId || undefined,
          schedule: schedulePayload,
        });
        setQueries((prev) => prev.map((q) => (q.id === editingId ? updated : q)));
      } else {
        // Place new queries at the end of the existing sequence so they
        // don't jump above user-reordered entries (default order=0 would
        // otherwise collide with the top of the list).
        const nextOrder = queries.length === 0
          ? 0
          : Math.max(...queries.map((q) => q.order ?? 0)) + 1;
        const created = await createCustomQuery({
          name: formName,
          system_prompt: formSystemPrompt || undefined,
          suggested_prompt: formSuggestedPrompt || undefined,
          default_mode: formDefaultMode,
          folder_id: formFolderId || undefined,
          order: nextOrder,
          schedule: schedulePayload,
        });
        setQueries((prev) => [...prev, created]);
      }
      resetForm();
      // Notify parent so its customQueries list refreshes immediately
      // rather than waiting for the 30-second polling tick.
      onCustomQueriesChanged?.();
    } catch (err) {
      console.error("Failed to save query:", err);
    } finally {
      setSaving(false);
    }
  }, [editingId, formName, formSystemPrompt, formSuggestedPrompt, formDefaultMode, formFolderId, formSchedule, queries, onCustomQueriesChanged]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteCustomQuery(id);
      setQueries((prev) => prev.filter((q) => q.id !== id));
      setDeleteConfirmId(null);
      if (editingId === id) resetForm();
      onCustomQueriesChanged?.();
    } catch (err) {
      console.error("Failed to delete query:", err);
    }
  }, [editingId, onCustomQueriesChanged]);

  const renderForm = () => (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <input
        type="text"
        placeholder="Query name"
        value={formName}
        onChange={(e) => setFormName(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          background: "var(--bg)",
          color: "var(--text-primary)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          fontSize: "13px",
          boxSizing: "border-box",
        }}
      />
      <div>
        <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "4px", display: "block" }}>
          System Prompt
        </label>
        <textarea
          placeholder="Optional system prompt..."
          value={formSystemPrompt}
          onChange={(e) => setFormSystemPrompt(e.target.value)}
          rows={9}
          style={{
            width: "100%",
            padding: "8px 10px",
            background: "var(--bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            fontSize: "12px",
            resize: "vertical",
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
      </div>
      <div>
        <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "4px", display: "block" }}>
          Suggested Prompt
        </label>
        <textarea
          placeholder="Optional suggested prompt..."
          value={formSuggestedPrompt}
          onChange={(e) => setFormSuggestedPrompt(e.target.value)}
          rows={2}
          style={{
            width: "100%",
            padding: "8px 10px",
            background: "var(--bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            fontSize: "12px",
            resize: "vertical",
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
      </div>
      <div>
        <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "4px", display: "block" }}>
          Default Mode
        </label>
        <div
          style={{
            display: "flex",
            gap: "2px",
            background: "var(--bg)",
            borderRadius: "var(--radius-md)",
            padding: "2px",
          }}
        >
          {modesForPicker.map((m) => (
            <button
              key={m.value}
              onClick={() => setFormDefaultMode(m.value)}
              style={{
                flex: 1,
                padding: "5px 4px",
                border: "none",
                borderRadius: "var(--radius-sm)",
                background: formDefaultMode === m.value ? "var(--accent)" : "transparent",
                color: formDefaultMode === m.value ? "#fff" : "var(--text-secondary)",
                fontSize: "11px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "4px", display: "block" }}>
          Folder
        </label>
        <select
          value={formFolderId}
          onChange={(e) => setFormFolderId(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 32px 8px 10px",
            background: "var(--bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            fontSize: "13px",
            // Strip native OS chrome so the control matches the rest of
            // the app's flat aesthetic. A custom chevron SVG is layered
            // on the right via background-image.
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            backgroundImage:
              'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'><path fill=\'%23808080\' d=\'M6 8L2 4h8z\'/></svg>")',
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 10px center",
            backgroundSize: "10px 10px",
            cursor: "pointer",
          }}
        >
          <option value="">None</option>
          {[...folders].sort(compareFolders).map(f => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
      </div>
      {/* --- Schedule (auto-run) --- */}
      {(() => {
        const scheduleDisabled = !formSuggestedPrompt.trim();
        const freq = formSchedule.frequency || "weekly";
        // Shared <select> chrome (matches the Folder select's flat look).
        const selectStyle: React.CSSProperties = {
          padding: "6px 28px 6px 10px",
          background: "var(--bg)",
          color: "var(--text-primary)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          fontSize: "13px",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'><path fill=\'%23808080\' d=\'M6 8L2 4h8z\'/></svg>")',
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 8px center",
          backgroundSize: "10px 10px",
          cursor: "pointer",
        };
        return (
          <div>
            <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>
              Schedule
            </label>
            {/* Toggle row */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: scheduleDisabled ? "not-allowed" : "pointer",
                opacity: scheduleDisabled ? 0.5 : 1,
                fontSize: "13px",
                color: "var(--text-primary)",
              }}
            >
              <input
                type="checkbox"
                checked={!!formSchedule.enabled}
                disabled={scheduleDisabled}
                onChange={(e) =>
                  setFormSchedule((s) => ({
                    ...s,
                    enabled: e.target.checked,
                    // Seed sensible defaults the first time it's turned on.
                    frequency: s.frequency || "weekly",
                    time_of_day: s.time_of_day || "09:00",
                    timezone: s.timezone || LOCAL_TIMEZONE,
                    day_of_week: s.day_of_week ?? 0,
                    day_of_month: s.day_of_month ?? 1,
                  }))
                }
              />
              Auto-run on a schedule
            </label>
            {scheduleDisabled && (
              <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "4px" }}>
                Add a suggested prompt — that&apos;s what runs on schedule.
              </div>
            )}
            {!scheduleDisabled && formSchedule.enabled && (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
                {/* Frequency segmented control */}
                <div>
                  <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "4px", display: "block" }}>
                    Frequency
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "2px",
                      background: "var(--bg)",
                      borderRadius: "var(--radius-md)",
                      padding: "2px",
                    }}
                  >
                    {SCHEDULE_FREQUENCIES.map((f) => (
                      <button
                        key={f.value}
                        onClick={() => setFormSchedule((s) => ({ ...s, frequency: f.value }))}
                        style={{
                          flex: 1,
                          padding: "5px 4px",
                          border: "none",
                          borderRadius: "var(--radius-sm)",
                          background: freq === f.value ? "var(--accent)" : "transparent",
                          color: freq === f.value ? "#fff" : "var(--text-secondary)",
                          fontSize: "11px",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Time + (conditional) day selectors */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
                  <div>
                    <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "4px", display: "block" }}>
                      Time
                    </label>
                    <input
                      type="time"
                      value={formSchedule.time_of_day || "09:00"}
                      onChange={(e) => setFormSchedule((s) => ({ ...s, time_of_day: e.target.value }))}
                      style={{
                        padding: "6px 10px",
                        background: "var(--bg)",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius-sm)",
                        fontSize: "13px",
                        colorScheme: "dark light",
                      }}
                    />
                  </div>
                  {freq === "weekly" && (
                    <div>
                      <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "4px", display: "block" }}>
                        Day of week
                      </label>
                      <select
                        value={formSchedule.day_of_week ?? 0}
                        onChange={(e) => setFormSchedule((s) => ({ ...s, day_of_week: Number(e.target.value) }))}
                        style={selectStyle}
                      >
                        {SCHEDULE_WEEKDAYS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {(freq === "monthly" || freq === "quarterly") && (
                    <div>
                      <label style={{ fontSize: "11px", color: "var(--text-secondary)", marginBottom: "4px", display: "block" }}>
                        Day of month
                      </label>
                      <select
                        value={formSchedule.day_of_month ?? 1}
                        onChange={(e) => setFormSchedule((s) => ({ ...s, day_of_month: Number(e.target.value) }))}
                        style={selectStyle}
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {/* Timezone (read-only display for v1; stored on the schedule) */}
                <div style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                  Timezone: {formSchedule.timezone || LOCAL_TIMEZONE}
                </div>
              </div>
            )}
          </div>
        );
      })()}
      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        <button className="btn-secondary" onClick={resetForm} style={{ fontSize: "12px", padding: "6px 12px" }}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={saving || !formName.trim()}
          style={{ fontSize: "12px", padding: "6px 12px" }}
        >
          {saving ? "Saving..." : editingId ? "Update" : "Create"}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
        Loading queries...
      </div>
    );
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {!showAdd && !editingId && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              className="btn-primary"
              onClick={startAdd}
              style={{ fontSize: "11px", padding: "4px 10px" }}
            >
              Add Query
            </button>
          </div>
        )}

        {editingId ? (
          renderForm()
        ) : showAdd ? (
          renderForm()
        ) : queries.length === 0 ? (
          <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
            No custom queries. Create one to use as a template.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "400px", overflowY: "auto" }}>
            {(() => {
              // Render in user-defined order. Up/down arrows mirror the
              // Default Providers reorder pattern in the Defaults tab —
              // single shared idiom across the Settings panel.
              const sorted = sortQueries(queries);
              return sorted.map((q, idx) => (
                <div
                  key={q.id}
                  style={{
                    padding: "10px 12px",
                    background: "var(--bg)",
                    borderRadius: "var(--radius-md)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)" }}>
                      {q.name}
                    </div>
                    {q.suggested_prompt && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-tertiary)",
                          marginTop: "2px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {q.suggested_prompt}
                      </div>
                    )}
                    <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "4px" }}>
                      {modesForPicker.find((m) => m.value === q.default_mode)?.label || resolveModeLabel(q.default_mode ?? "auto", modes)}
                      {formatScheduleSummary(q.schedule) && (
                        <span style={{ color: "var(--accent)" }}>
                          {"  ·  Scheduled: "}{formatScheduleSummary(q.schedule)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "1px", flexShrink: 0 }}>
                    <button
                      className="btn-ghost"
                      disabled={idx === 0}
                      onClick={() => moveQuery(idx, -1)}
                      style={{ padding: "0 4px", fontSize: "10px", lineHeight: 1, opacity: idx === 0 ? 0.3 : 1, color: "var(--text-tertiary)" }}
                      aria-label="Move up"
                    >&#9650;</button>
                    <button
                      className="btn-ghost"
                      disabled={idx === sorted.length - 1}
                      onClick={() => moveQuery(idx, 1)}
                      style={{ padding: "0 4px", fontSize: "10px", lineHeight: 1, opacity: idx === sorted.length - 1 ? 0.3 : 1, color: "var(--text-tertiary)" }}
                      aria-label="Move down"
                    >&#9660;</button>
                  </div>
                  <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                    <button
                      className="btn-ghost"
                      onClick={() => startEdit(q)}
                      style={{ padding: "4px", color: "var(--text-tertiary)" }}
                      aria-label="Edit query"
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" />
                      </svg>
                    </button>
                    <button
                      className="btn-ghost"
                      onClick={() => setDeleteConfirmId(q.id)}
                      style={{ padding: "4px", color: "var(--text-tertiary)" }}
                      aria-label="Delete query"
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M4 4l8 8M12 4l-8 8" />
                      </svg>
                    </button>
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteConfirmId !== null}
        title="Delete Query"
        message="Are you sure you want to delete this custom query?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => deleteConfirmId && handleDelete(deleteConfirmId)}
        onCancel={() => setDeleteConfirmId(null)}
        variant="danger"
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function SettingsPanel({ isOpen, onClose, initialTab, onCustomQueriesChanged }: SettingsPanelProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>("account");

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab as SettingsTab);
  }, [initialTab]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Export for Admin state
  const [adminExportExpanded, setAdminExportExpanded] = useState(false);
  const [adminExportSessions, setAdminExportSessions] = useState<{ id: string; title: string; created_at: string }[]>([]);
  const [adminExportSelected, setAdminExportSelected] = useState<Set<string>>(new Set());
  const [adminExportLoading, setAdminExportLoading] = useState(false);
  const [adminExportSubmitting, setAdminExportSubmitting] = useState(false);
  const [adminExportStatus, setAdminExportStatus] = useState<string | null>(null);

  // Feedback state
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState<string | null>(null);

  // Account display-name edit state. `nameDraft` is the input value;
  // `nameSaving` guards the Save button; `nameStatus` shows brief
  // success/error feedback.
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameStatus, setNameStatus] = useState<string | null>(null);

  // Admin-debug opt-in state (v2 users only; reflects X-DEK wrap state)
  const [adminDebugEnabled, setAdminDebugEnabled] = useState(false);
  const [adminDebugAvailable, setAdminDebugAvailable] = useState(false);
  const [adminDebugSubmitting, setAdminDebugSubmitting] = useState(false);
  const [adminDebugStatus, setAdminDebugStatus] = useState<string | null>(null);

  // Change-password modal state (v2 only)
  const [isV2, setIsV2] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [cpError, setCpError] = useState<string | null>(null);
  const [cpSubmitting, setCpSubmitting] = useState(false);

  // Regenerate recovery codes confirmation state
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  // Codes shown after change-password / regenerate success. When non-null
  // renders RecoveryCodesDisplay as a full-page overlay above the modal.
  const [postActionCodes, setPostActionCodes] = useState<string[] | null>(null);
  const [showAIDisclosure, setShowAIDisclosure] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const state = getAuthState();
    if (!state) {
      setAdminDebugAvailable(false);
      return;
    }
    setAdminDebugAvailable(
      state.schemeVersion === "v2" && (state.adminPubkeys?.length ?? 0) > 0
    );
    setAdminDebugEnabled(state.adminDebugEnabled ?? false);
    setIsV2(state.schemeVersion === "v2");
  }, [isOpen]);

  const { email, name: accountName, isAdmin, logout, setName: setAccountName } = useAuth();
  const { providers, modes } = useProviders();
  const modesForPicker = modeOptions(modes);
  const defaultModesForPicker = defaultModeOptions(modes);
  const {
    preferences,
    updatePreferences,
    updateCustomInstructions,
    deleteCustomInstructionItem,
    clearCustomInstructionsAlert,
  } = usePreferences();
  const customInstructionItems = preferences?.custom_instruction_items ?? [];

  // Seed the name input from auth state when the panel opens, and clear
  // any stale save status. Re-runs if the cached name changes.
  useEffect(() => {
    if (!isOpen) return;
    setNameDraft(accountName);
    setNameStatus(null);
  }, [isOpen, accountName]);

  // Fetch the live account name whenever the Account section opens. The name
  // otherwise only reaches the client via the login / token-refresh response,
  // so a session established before the account was named shows a blank Name
  // field. Re-running on every Account-section open keeps it live (covers
  // pre-name logins, future accounts, and admin-side name changes). On
  // success: push into auth state via setName(...) — which also reseeds
  // nameDraft through the effect above — and seed nameDraft directly so the
  // input is correct even if the cached name was already in sync. Failures
  // are swallowed so the Account view never blocks or errors.
  useEffect(() => {
    if (!isOpen || activeTab !== "account") return;
    let cancelled = false;
    getAccountName()
      .then(({ name }) => {
        if (cancelled) return;
        setAccountName(name);
        setNameDraft(name);
      })
      .catch(() => {
        // Fail silently — never block or error the Account view.
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab, setAccountName]);

  // Save the edited display name. PUT /account/name trims server-side and
  // rejects empty / >100-char with HTTP 400; the returned name is the
  // canonical value we push back into auth state.
  const handleSaveName = useCallback(async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === accountName) return;
    setNameSaving(true);
    setNameStatus(null);
    try {
      const result = await updateAccountName(trimmed);
      setAccountName(result.name);
      setNameDraft(result.name);
      setNameStatus("Name saved.");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      setNameStatus(`Failed: ${msg}`);
    } finally {
      setNameSaving(false);
    }
  }, [nameDraft, accountName, setAccountName]);

  // Admin badge polling — only active while Settings modal is open AND user
  // is admin. Non-admin users never hit the endpoint.
  const { counts: adminBadgeCounts, markTabRead: markAdminTabRead, refresh: refreshAdminBadges } =
    useAdminBadges(isOpen, isAdmin);

  const [expandedDefaults, setExpandedDefaults] = useState<Record<string, boolean>>({});
  const [customInstructions, setCustomInstructions] = useState("");
  const [customInstructionsSaving, setCustomInstructionsSaving] = useState(false);
  const [customInstructionsError, setCustomInstructionsError] = useState<string | null>(null);
  const [deleteCustomInstructionConfirmId, setDeleteCustomInstructionConfirmId] = useState<string | null>(null);

  const router = useRouter();
  const handleLogout = useCallback(() => {
    onClose();
    logout();
    router.replace("/login");
  }, [logout, onClose, router]);

  const handleDeleteAccount = useCallback(() => {
    // TODO: implement account deletion API call
    setShowDeleteConfirm(false);
  }, []);

  // --- Change password ---
  const cpPasswordValid = cpNew.length >= 12;
  const cpPasswordsMatch = cpNew === cpConfirm;
  const cpFormValid =
    cpCurrent.length > 0 && cpPasswordValid && cpPasswordsMatch && cpCurrent !== cpNew;

  const resetChangePasswordForm = useCallback(() => {
    setCpCurrent("");
    setCpNew("");
    setCpConfirm("");
    setCpError(null);
    setCpSubmitting(false);
  }, []);

  const handleChangePasswordSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!cpFormValid || cpSubmitting) return;
      setCpError(null);
      setCpSubmitting(true);
      try {
        const { recoveryCodes } = await changePasswordV2(cpCurrent, cpNew);
        setShowChangePassword(false);
        resetChangePasswordForm();
        setPostActionCodes(recoveryCodes);
      } catch (err) {
        setCpError(err instanceof Error ? err.message : "Change password failed.");
        setCpSubmitting(false);
      }
    },
    [cpCurrent, cpNew, cpFormValid, cpSubmitting, resetChangePasswordForm]
  );

  // --- Regenerate recovery codes ---
  const handleRegenerateCodes = useCallback(async () => {
    if (regenerating) return;
    setRegenerating(true);
    setRegenerateError(null);
    try {
      const { recoveryCodes } = await rotateRecoveryCodesV2();
      setShowRegenerateConfirm(false);
      setPostActionCodes(recoveryCodes);
    } catch (err) {
      setRegenerateError(err instanceof Error ? err.message : "Regenerate failed.");
    } finally {
      setRegenerating(false);
    }
  }, [regenerating]);

  const handleModeChange = useCallback(
    async (mode: string) => {
      await updatePreferences({ ...preferences, default_mode: mode });
    },
    [preferences, updatePreferences]
  );

  const handleToggleDefaultProvider = useCallback(
    async (providerId: string) => {
      const current = preferences?.default_providers || [];
      let next: string[];
      if (current.includes(providerId)) {
        next = current.filter((p) => p !== providerId);
        if (next.length === 0) return; // Don't allow empty
      } else {
        next = [...current, providerId];
      }
      await updatePreferences({ ...preferences, default_providers: next });
    },
    [preferences, updatePreferences]
  );

  const handleCustomInstructionsSave = useCallback(async () => {
    setCustomInstructionsSaving(true);
    setCustomInstructionsError(null);
    try {
      const result = await updateCustomInstructions(customInstructions);
      if (result.alert?.verdict !== "rejected") {
        setCustomInstructions("");
      }
    } catch (err) {
      setCustomInstructionsError(err instanceof Error ? err.message : "Failed to save custom instructions.");
    } finally {
      setCustomInstructionsSaving(false);
    }
  }, [customInstructions, updateCustomInstructions]);

  const handleCustomInstructionDelete = useCallback(async (id: string) => {
    setCustomInstructionsError(null);
    try {
      await deleteCustomInstructionItem(id);
      setDeleteCustomInstructionConfirmId(null);
    } catch (err) {
      setCustomInstructionsError(err instanceof Error ? err.message : "Failed to delete custom instruction.");
    }
  }, [deleteCustomInstructionItem]);

  const tabs: { key: SettingsTab; label: string }[] = [
    { key: "account", label: "Account" },
    { key: "defaults", label: "Defaults" },
    { key: "memories", label: "Memory" },
    { key: "instructions", label: "Instructions" },
    { key: "queries", label: "Queries" },
    { key: "usage", label: "Usage" },
    { key: "help", label: "Help" },
    ...(isAdmin ? [{ key: "admin" as SettingsTab, label: "Admin" }] : []),
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Settings" fixedSize>
        {/* Tab bar — two rows: first row 4 tabs, second row remainder */}
        <div
          className="settings-tabs-bar"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            background: "var(--bg)",
            borderRadius: "var(--radius-md)",
            padding: "2px",
            marginBottom: "16px",
          }}
        >
          {[tabs.slice(0, 4), tabs.slice(4)].map((rowTabs, rowIdx) => (
            <div key={rowIdx} style={{ display: "flex", gap: "2px" }}>
              {rowTabs.map((tab) => {
                const showAdminBadge =
                  tab.key === "admin" && adminBadgeCounts.total > 0;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={
                      activeTab === tab.key ? "settings-tab settings-tab-active" : "settings-tab"
                    }
                    style={{
                      flex: 1,
                      padding: "6px 0",
                      border: "none",
                      borderRadius: "var(--radius-sm)",
                      background:
                        activeTab === tab.key ? "var(--surface-hover)" : "transparent",
                      color:
                        activeTab === tab.key
                          ? "var(--text-primary)"
                          : "var(--text-secondary)",
                      fontSize: "12px",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all var(--transition-fast)",
                      position: "relative",
                    }}
                  >
                    {tab.label}
                    {showAdminBadge && (
                      <span
                        style={{
                          ...BADGE_PILL_STYLE,
                          position: "absolute",
                          top: "2px",
                          right: "6px",
                        }}
                      >
                        {formatBadgeCount(adminBadgeCounts.total)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Tab content */}
        <div className="settings-panel">
          {activeTab === "account" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* ───── Export ───── */}
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  marginBottom: "-8px",
                }}
              >
                Export
              </div>

              {/* Export for Admin */}
              <div>
                <button
                  className="btn-secondary"
                  onClick={async () => {
                    if (!adminExportExpanded) {
                      setAdminExportExpanded(true);
                      setAdminExportStatus(null);
                      if (adminExportSessions.length === 0) {
                        setAdminExportLoading(true);
                        try {
                          const resp = await fetchAllSessions(0, 500);
                          setAdminExportSessions(resp.sessions);
                        } catch {
                          setAdminExportStatus("Failed to load sessions");
                        } finally {
                          setAdminExportLoading(false);
                        }
                      }
                    } else {
                      setAdminExportExpanded(false);
                    }
                  }}
                  style={{ width: "100%" }}
                >
                  {adminExportExpanded ? "Hide Export History Items for Admin" : "Export History Items for Admin"}
                </button>
                {adminExportStatus && !adminExportExpanded && (
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: adminExportStatus.startsWith("Export failed")
                        ? "var(--error)"
                        : "var(--success, #4ade80)",
                    }}
                  >
                    {adminExportStatus}
                  </div>
                )}
                {adminExportExpanded && (
                  <div
                    style={{
                      marginTop: "8px",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)",
                      padding: "12px",
                      background: "var(--bg)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--text-secondary)",
                        marginBottom: "10px",
                        lineHeight: 1.45,
                      }}
                    >
                      Selected chats are re-encrypted with a server key so the administrator can read them for troubleshooting. Recent app diagnostic events (metadata only &mdash; session IDs, counts, state transitions; no prompt or response text) may also be included to help reproduce issues.
                    </div>
                    {adminExportLoading ? (
                      <div style={{ color: "var(--text-secondary)", fontSize: "13px", textAlign: "center", padding: "16px 0" }}>
                        Loading sessions...
                      </div>
                    ) : adminExportSessions.length === 0 ? (
                      <div style={{ color: "var(--text-secondary)", fontSize: "13px", textAlign: "center", padding: "16px 0" }}>
                        No sessions found
                      </div>
                    ) : (
                      <>
                        {/* Select All */}
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "var(--text-primary)",
                            paddingBottom: "8px",
                            borderBottom: "1px solid var(--border)",
                            marginBottom: "4px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={adminExportSelected.size === adminExportSessions.length && adminExportSessions.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAdminExportSelected(new Set(adminExportSessions.map((s) => s.id)));
                              } else {
                                setAdminExportSelected(new Set());
                              }
                            }}
                            style={{ accentColor: "var(--accent)", width: "16px", height: "16px" }}
                          />
                          Select All ({adminExportSessions.length})
                        </label>
                        {/* Session list */}
                        <div
                          style={{
                            maxHeight: "300px",
                            overflowY: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                          }}
                        >
                          {adminExportSessions.map((session) => (
                            <label
                              key={session.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer",
                                padding: "6px 4px",
                                borderRadius: "var(--radius-sm)",
                                fontSize: "13px",
                                color: "var(--text-primary)",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={adminExportSelected.has(session.id)}
                                onChange={() => {
                                  setAdminExportSelected((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(session.id)) {
                                      next.delete(session.id);
                                    } else {
                                      next.add(session.id);
                                    }
                                    return next;
                                  });
                                }}
                                style={{ accentColor: "var(--accent)", width: "16px", height: "16px", flexShrink: 0 }}
                              />
                              <span
                                style={{
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {session.title || "Untitled"}
                              </span>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "var(--text-tertiary)",
                                  flexShrink: 0,
                                }}
                              >
                                {new Date(session.created_at).toLocaleDateString()}
                              </span>
                            </label>
                          ))}
                        </div>
                        {/* Export button */}
                        <button
                          className="btn-primary"
                          disabled={adminExportSelected.size === 0 || adminExportSubmitting}
                          onClick={async () => {
                            setAdminExportSubmitting(true);
                            setAdminExportStatus(null);
                            try {
                              const result = await exportForAdmin(Array.from(adminExportSelected));
                              setAdminExportStatus(
                                `Exported ${result.exported_sessions} session${result.exported_sessions !== 1 ? "s" : ""} for admin`
                              );
                              setAdminExportExpanded(false);
                              setAdminExportSelected(new Set());
                            } catch (err: any) {
                              setAdminExportStatus(`Export failed: ${err.message || "Unknown error"}`);
                            } finally {
                              setAdminExportSubmitting(false);
                            }
                          }}
                          style={{ width: "100%", marginTop: "10px" }}
                        >
                          {adminExportSubmitting
                            ? "Exporting..."
                            : `Export ${adminExportSelected.size} Selected`}
                        </button>
                      </>
                    )}
                    {adminExportStatus && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "12px",
                          color: adminExportStatus.startsWith("Export failed")
                            ? "var(--error)"
                            : "var(--success, #4ade80)",
                        }}
                      >
                        {adminExportStatus}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ───── Account ───── */}
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  marginTop: "4px",
                  marginBottom: "-8px",
                }}
              >
                Account
              </div>

              {/* 1. Signed in as — primary color so it matches the prominence
                   of the buttons below without implying tappability. */}
              {email && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "13px",
                    color: "var(--text-primary)",
                  }}
                >
                  <span>Signed in as</span>
                  <span
                    style={{
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginLeft: "8px",
                    }}
                  >
                    {email}
                  </span>
                </div>
              )}

              {/* 1b. Editable display name — sits directly under "Signed in
                   as". Save enabled only when the input differs from the
                   saved name (and is non-empty). */}
              <div
                style={{
                  padding: "10px 12px",
                  background: "var(--bg)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginBottom: "6px",
                  }}
                >
                  Name
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="text"
                    value={nameDraft}
                    onChange={(e) => {
                      setNameDraft(e.target.value);
                      setNameStatus(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveName();
                    }}
                    placeholder="Your name"
                    maxLength={100}
                    disabled={nameSaving}
                    style={{
                      flex: 1,
                      padding: "8px 10px",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)",
                      background: "var(--surface)",
                      color: "var(--text-primary)",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                  <button
                    className="btn-primary"
                    onClick={handleSaveName}
                    disabled={
                      nameSaving ||
                      !nameDraft.trim() ||
                      nameDraft.trim() === accountName
                    }
                    style={{
                      fontSize: "12px",
                      padding: "6px 14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {nameSaving ? "Saving..." : "Save"}
                  </button>
                </div>
                {nameStatus && (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "12px",
                      color: nameStatus.startsWith("Failed")
                        ? "var(--error, #ef4444)"
                        : "var(--success, #4ade80)",
                    }}
                  >
                    {nameStatus}
                  </div>
                )}
              </div>

              {/* 2. Share data with admin — renamed from "Share logs & data
                   with admin for debugging" to fit a single line. */}
              {adminDebugAvailable && (
                <div
                  style={{
                    padding: "12px",
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      cursor: adminDebugSubmitting ? "wait" : "pointer",
                      fontSize: "13px",
                      color: "var(--text-primary)",
                      lineHeight: 1.45,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={adminDebugEnabled}
                      disabled={adminDebugSubmitting}
                      onChange={async (e) => {
                        const nextEnabled = e.target.checked;
                        setAdminDebugSubmitting(true);
                        setAdminDebugStatus(null);
                        try {
                          if (nextEnabled) {
                            await adminDebugEnable();
                          } else {
                            await adminDebugDisable();
                          }
                          setAdminDebugEnabled(nextEnabled);
                          setAdminDebugStatus(
                            nextEnabled
                              ? "Admin access enabled."
                              : "Admin access disabled."
                          );
                        } catch (err: unknown) {
                          const msg =
                            err instanceof Error ? err.message : "Unknown error";
                          setAdminDebugStatus(`Failed: ${msg}`);
                        } finally {
                          setAdminDebugSubmitting(false);
                        }
                      }}
                      style={{
                        accentColor: "var(--accent)",
                        width: "16px",
                        height: "16px",
                        flexShrink: 0,
                        marginTop: "2px",
                      }}
                    />
                    <span>
                      <span style={{ fontWeight: 600 }}>
                        Share data with admin
                      </span>
                      <span
                        style={{
                          display: "block",
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          marginTop: "2px",
                        }}
                      >
                        When on, your encryption key is wrapped so the admin can
                        decrypt your chats and diagnostic logs for troubleshooting.
                        You can turn this off anytime.
                      </span>
                    </span>
                  </label>
                  {adminDebugStatus && (
                    <div
                      style={{
                        marginTop: "8px",
                        marginLeft: "26px",
                        fontSize: "12px",
                        color: adminDebugStatus.startsWith("Failed")
                          ? "var(--error)"
                          : "var(--success, #4ade80)",
                      }}
                    >
                      {adminDebugStatus}
                    </div>
                  )}
                </div>
              )}

              {/* 3 + 4. v2 only: change password + regenerate recovery codes. */}
              {isV2 && (
                <>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      resetChangePasswordForm();
                      setShowChangePassword(true);
                    }}
                  >
                    Change Password
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setRegenerateError(null);
                      setShowRegenerateConfirm(true);
                    }}
                  >
                    Regenerate Recovery Codes
                  </button>
                </>
              )}

              {/* 5. Log Out */}
              <button className="btn-danger" onClick={handleLogout}>
                Log Out
              </button>

              {/* ───── Send Feedback ───── */}
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  marginTop: "4px",
                  marginBottom: "-8px",
                }}
              >
                Send Feedback
              </div>
              <div>
                <textarea
                  value={feedbackText}
                  onChange={(e) => { setFeedbackText(e.target.value); setFeedbackStatus(null); }}
                  placeholder="Share feedback, report issues, or request features..."
                  rows={3}
                  disabled={feedbackSubmitting}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    background: "var(--bg)",
                    color: "var(--text-primary)",
                    fontSize: "13px",
                    resize: "vertical",
                    outline: "none",
                    fontFamily: "inherit",
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "6px" }}>
                  <button
                    className="btn-primary"
                    disabled={!feedbackText.trim() || feedbackSubmitting}
                    onClick={async () => {
                      setFeedbackSubmitting(true);
                      setFeedbackStatus(null);
                      try {
                        await submitFeedback(feedbackText.trim());
                        setFeedbackText("");
                        setFeedbackStatus("Feedback sent. Thank you!");
                      } catch (e: any) {
                        setFeedbackStatus("Failed to send feedback.");
                      } finally {
                        setFeedbackSubmitting(false);
                      }
                    }}
                    style={{ fontSize: "12px", padding: "6px 14px" }}
                  >
                    {feedbackSubmitting ? "Sending..." : "Send"}
                  </button>
                  {feedbackStatus && (
                    <span style={{ fontSize: "12px", color: feedbackStatus.startsWith("Failed") ? "var(--error, #ef4444)" : "var(--success, #4ade80)" }}>
                      {feedbackStatus}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  marginTop: "4px",
                  marginBottom: "-8px",
                }}
              >
                Privacy
              </div>
              <button className="btn-secondary" onClick={() => setShowAIDisclosure(true)}>
                AI Data Sharing
              </button>

              {/* ───── About ───── */}
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  marginTop: "4px",
                  marginBottom: "-8px",
                }}
              >
                About
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 12px",
                  background: "var(--bg)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                  fontSize: "13px",
                  color: "var(--text-primary)",
                }}
              >
                <span>Version</span>
                <span style={{ color: "var(--text-secondary)" }}>1.0 (1)</span>
              </div>

              <button
                className="btn-secondary"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete Account
              </button>
            </div>
          )}

          {activeTab === "defaults" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Default mode */}
              <div>
                <label style={{ marginBottom: "8px", display: "block" }}>
                  Default Mode
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    background: "var(--bg)",
                    borderRadius: "var(--radius-md)",
                    padding: "2px",
                  }}
                >
                  {defaultModesForPicker.map((mode) => (
                    <button
                      key={mode.value}
                      onClick={() => handleModeChange(mode.value)}
                      style={{
                        flex: 1,
                        padding: "6px 4px",
                        border: "none",
                        borderRadius: "var(--radius-sm)",
                        background:
                          preferences?.default_mode === mode.value
                            ? "var(--accent)"
                            : "transparent",
                        color:
                          preferences?.default_mode === mode.value
                            ? "#ffffff"
                            : "var(--text-secondary)",
                        fontSize: "12px",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all var(--transition-fast)",
                      }}
                    >
                      {mode.label}
                    </button>
                  ))}
                  {preferences?.default_mode === "deep_research" && (
                    <button
                      disabled
                      style={{
                        flex: 1,
                        padding: "6px 4px",
                        border: "none",
                        borderRadius: "var(--radius-sm)",
                        background: "var(--accent)",
                        color: "#ffffff",
                        fontSize: "12px",
                        fontWeight: 500,
                        opacity: 0.7,
                      }}
                    >
                      {resolveModeLabel("deep_research", modes)} (legacy default)
                    </button>
                  )}
                </div>
              </div>

              {preferences?.deep_research_library_available && (
                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      color: "var(--text-primary)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={preferences.deep_research_use_library ?? true}
                      onChange={(event) => void updatePreferences({
                        ...preferences,
                        deep_research_use_library: event.target.checked,
                      })}
                    />
                    Use library in Deep Research
                  </label>
                  <p style={{ fontSize: "11px", color: "var(--text-tertiary)", margin: "4px 0 0" }}>
                    Off: Deep Research runs the way other users get it, without the library.
                  </p>
                </div>
              )}

              {/* Default providers — with reorder */}
              <div>
                <label style={{ marginBottom: "8px", display: "block" }}>
                  Default Providers
                </label>
                <p style={{ fontSize: "11px", color: "var(--text-tertiary)", margin: "0 0 6px 0" }}>
                  Click to toggle. Use arrows to reorder.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {(() => {
                    const selected = preferences?.default_providers || providers.map(p => p.id);
                    const savedOrder = preferences?.provider_order || selected;
                    const order = [
                      ...savedOrder.filter(id => selected.includes(id)),
                      ...selected.filter(id => !savedOrder.includes(id)),
                    ];
                    const orderedProviders = [
                      ...order.map(id => providers.find(p => p.id === id)).filter(Boolean),
                      ...providers.filter(p => !order.includes(p.id)),
                    ] as typeof providers;
                    return orderedProviders.map((provider, idx) => {
                      const isSelected = order.includes(provider.id);
                      return (
                        <div key={provider.id} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <button
                            onClick={() => handleToggleDefaultProvider(provider.id)}
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "7px 12px",
                              border: `1px solid ${isSelected ? "var(--accent)" : "var(--border)"}`,
                              borderRadius: "var(--radius-md)",
                              background: isSelected ? "var(--accent-muted)" : "transparent",
                              color: "var(--text-primary)",
                              fontSize: "13px",
                              cursor: "pointer",
                            }}
                          >
                            <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: provider.color, flexShrink: 0 }} />
                            {provider.display_name}
                          </button>
                          <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                            <button
                              className="btn-ghost"
                              disabled={idx === 0}
                              onClick={async () => {
                                const cur = [...order];
                                const i = cur.indexOf(provider.id);
                                if (i > 0) { [cur[i - 1], cur[i]] = [cur[i], cur[i - 1]]; }
                                await updatePreferences({ ...preferences, default_providers: cur, provider_order: cur });
                              }}
                              style={{ padding: "0 4px", fontSize: "10px", lineHeight: 1, opacity: idx === 0 ? 0.3 : 1 }}
                              aria-label="Move up"
                            >&#9650;</button>
                            <button
                              className="btn-ghost"
                              disabled={idx === orderedProviders.length - 1}
                              onClick={async () => {
                                const cur = [...order];
                                const i = cur.indexOf(provider.id);
                                if (i < cur.length - 1) { [cur[i], cur[i + 1]] = [cur[i + 1], cur[i]]; }
                                await updatePreferences({ ...preferences, default_providers: cur, provider_order: cur });
                              }}
                              style={{ padding: "0 4px", fontSize: "10px", lineHeight: 1, opacity: idx === orderedProviders.length - 1 ? 0.3 : 1 }}
                              aria-label="Move down"
                            >&#9660;</button>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Default Single Provider */}
              <div>
                <label style={{ marginBottom: "8px", display: "block" }}>
                  Default Single Provider
                </label>
                <p style={{ fontSize: "11px", color: "var(--text-tertiary)", margin: "0 0 8px 0" }}>
                  When single provider mode is activated, this provider will be used.
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    background: "var(--bg)",
                    borderRadius: "var(--radius-md)",
                    padding: "2px",
                  }}
                >
                  {providers.map((p) => {
                    const singleProvider = preferences?.default_providers?.[0] || providers[0]?.id;
                    const isSelected = singleProvider === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={async () => {
                          const current = preferences?.default_providers || [];
                          const next = current.includes(p.id)
                            ? [p.id, ...current.filter((id) => id !== p.id)]
                            : [p.id, ...current];
                          await updatePreferences({ ...preferences, default_providers: next });
                        }}
                        style={{
                          flex: 1,
                          padding: "6px 4px",
                          border: "none",
                          borderRadius: "var(--radius-sm)",
                          background: isSelected ? p.color : "transparent",
                          color: isSelected ? "#fff" : "var(--text-secondary)",
                          fontSize: "12px",
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all var(--transition-fast)",
                        }}
                      >
                        {p.display_name}
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {activeTab === "instructions" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <label style={{ marginBottom: "4px", display: "block" }}>Custom Instructions</label>
                <p style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.5, margin: 0 }}>
                  Trio checks what you write against its own instructions. If something conflicts, it will be adjusted and Trio will tell you. Standing facts about you may be moved to Memory.
                </p>
              </div>

              {preferences?.custom_instructions_alert && (
                <div style={{ padding: "12px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
                    <div style={{ flex: 1, fontSize: "12px", color: "var(--text-primary)", lineHeight: 1.5 }}>
                      {preferences.custom_instructions_alert.summary}
                      {preferences.custom_instructions_alert.changes.length > 0 && (
                        <ul style={{ margin: "6px 0 0", paddingLeft: "18px", color: "var(--text-secondary)" }}>
                          {preferences.custom_instructions_alert.changes.map((change, index) => <li key={index}>{change}</li>)}
                        </ul>
                      )}
                      {preferences.custom_instructions_alert.memories_added.length > 0 && (
                        <p style={{ margin: "6px 0 0", color: "var(--text-secondary)" }}>
                          Added to Memory: {preferences.custom_instructions_alert.memories_added.join(", ")}
                        </p>
                      )}
                    </div>
                    <button
                      className="btn-ghost"
                      onClick={() => { void clearCustomInstructionsAlert(); }}
                      style={{ fontSize: "11px", padding: "2px 6px", flexShrink: 0 }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              <textarea
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                rows={10}
                disabled={customInstructionsSaving}
                placeholder="Add one instruction for Trio..."
                style={{ width: "100%", padding: "8px 10px", background: "var(--bg)", color: "var(--text-primary)", border: "2px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: "12px", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
              />
              {customInstructionsError && <p style={{ margin: 0, color: "var(--error, #ef4444)", fontSize: "12px" }}>{customInstructionsError}</p>}
              <button className="btn-primary" onClick={() => { void handleCustomInstructionsSave(); }} disabled={customInstructionsSaving}>
                {customInstructionsSaving ? "Saving..." : "Save"}
              </button>

              <div>
                <label style={{ marginBottom: "4px", display: "block" }}>Your instructions</label>
                {customInstructionItems.length === 0 ? (
                  <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
                    No instructions yet.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "400px", overflowY: "auto" }}>
                    {customInstructionItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          padding: "10px 12px",
                          background: "var(--bg)",
                          borderRadius: "var(--radius-md)",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0, fontSize: "13px", color: "var(--text-primary)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                          {item.text}
                        </div>
                        <button
                          className="btn-ghost"
                          onClick={() => setDeleteCustomInstructionConfirmId(item.id)}
                          style={{ padding: "4px", color: "var(--text-tertiary)", flexShrink: 0 }}
                          aria-label="Delete instruction"
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M4 4l8 8M12 4l-8 8" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <ConfirmDialog
                isOpen={deleteCustomInstructionConfirmId !== null}
                title="Delete Instruction"
                message="Are you sure you want to delete this custom instruction?"
                confirmLabel="Delete"
                cancelLabel="Cancel"
                onConfirm={() => {
                  if (deleteCustomInstructionConfirmId) {
                    void handleCustomInstructionDelete(deleteCustomInstructionConfirmId);
                  }
                }}
                onCancel={() => setDeleteCustomInstructionConfirmId(null)}
                variant="danger"
              />
            </div>
          )}

          {activeTab === "memories" && <MemoriesTab />}

          {activeTab === "queries" && <QueriesTab onCustomQueriesChanged={onCustomQueriesChanged} />}

          {activeTab === "usage" && <UsagePanel />}

          {activeTab === "help" && <HelpContent />}

          {activeTab === "admin" && isAdmin && (
            <AdminPanel
              badgeCounts={adminBadgeCounts}
              onMarkTabRead={markAdminTabRead}
              onBadgeRefresh={() => { void refreshAdminBadges(); }}
            />
          )}
        </div>
      </Modal>

      <Modal isOpen={showAIDisclosure} onClose={() => setShowAIDisclosure(false)} title="AI Data Sharing">
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", fontSize: "13px", lineHeight: 1.5, color: "var(--text-primary)", maxHeight: "70vh", overflowY: "auto", paddingRight: "4px" }}>
          <p style={{ margin: 0 }}>Trio compares responses from three independent AI providers. Before you start, here&apos;s exactly what is shared with them.</p>

          <div>
            <strong>Who receives your data</strong>
            <p style={{ margin: "6px 0" }}>Your messages are sent only to the provider(s) you select for each query:</p>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              <li>OpenAI — api.openai.com</li>
              <li>Anthropic — api.anthropic.com</li>
              <li>Google AI — generativelanguage.googleapis.com</li>
            </ul>
            <p style={{ margin: "6px 0 0" }}>You can change your selection at any time. A message is sent only to the providers active for that send.</p>
          </div>

          <div>
            <strong>What is sent with each message</strong>
            <ul style={{ margin: "6px 0", paddingLeft: "20px" }}>
              <li>Your prompt</li>
              <li>Recent conversation history in the active chat</li>
              <li>Files you attach (documents, images, audio)</li>
              <li>Memories you&apos;ve set in Settings</li>
              <li>The system prompt and model parameters Trio assembles for the request</li>
            </ul>
            <strong>When Auto mode is selected</strong>
            <p style={{ margin: "6px 0 0" }}>Your prompt and recent history are also sent to Anthropic Claude Haiku 4.5 to decide which tier (Instant / Low / Medium / High / Max / Deep Research) to route each provider to. The classifier sees the same content as the providers themselves; nothing additional is shared.</p>
            <p style={{ margin: "6px 0 0" }}>On follow-up queries to long-running modes (Pro / Deep Research) in sessions with prior attachments, an additional Haiku 4.5 call uses the filenames of those attachments and your follow-up prompt to decide which prior attachments to re-include for context. Filenames only — file contents are not sent to the relevance classifier.</p>
          </div>

          <div>
            <strong>Other services Trio uses</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: "20px" }}>
              <li><strong>Product links</strong> — when an answer recommends a product, Trio&apos;s server looks up that product&apos;s name on Amazon (amazon.com) and Bing (bing.com) to find a link. Only the product name is sent — never your message or your account details.</li>
              <li><strong>Stock charts</strong> — when an answer includes a stock chart, Trio&apos;s server requests price data for the ticker symbol from Yahoo Finance. Only the ticker symbol is sent.</li>
              <li><strong>Email</strong> — account emails (invitations, password-reset codes and account-deletion notices) are sent through Resend, which receives your email address and the email&apos;s contents. Replies to those emails are also received through Resend.</li>
            </ul>
          </div>

          <div>
            <strong>Text-to-speech (read-aloud + voice mode)</strong>
            <p style={{ margin: "6px 0 0" }}>Trio uses OpenAI&apos;s text-to-speech to read responses aloud when you tap a play button or use voice mode. The response text (or, with auto-judge, the best-of synthesis text) is sent to OpenAI&apos;s audio/speech endpoint so it can return spoken audio.</p>
            <p style={{ margin: "6px 0 0" }}>OpenAI processes this text under their API terms. It is not used for training and is retained for ≤30 days for abuse monitoring per OpenAI&apos;s API data policy. No identifiers tying the audio request to your account are sent.</p>
            <p style={{ margin: "6px 0 0" }}>Voice mode also uses your microphone for transcription. Audio captured by the microphone is processed on-device by Apple&apos;s speech recognizer; only the final transcribed text is sent to providers.</p>
          </div>

          <div>
            <strong>What is not sent</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: "20px" }}>
              <li>Your email address or account ID</li>
              <li>Your device identifier or advertising identifier</li>
              <li>Your name, contacts, or location</li>
              <li>Your IP address — providers see Trio&apos;s server IP, not your device&apos;s</li>
              <li>Your password, recovery codes, or biometric data — these never leave your device</li>
            </ul>
          </div>

          <div>
            <strong>How providers handle your data</strong>
            <p style={{ margin: "6px 0 0" }}>Each provider operates under its own privacy policy.</p>
            <p style={{ margin: "6px 0 0" }}>As of April 2026, OpenAI, Anthropic, and Google all state that they do not train their generative models on data submitted through their paid commercial APIs by default. These policies vary by product tier and may change over time.</p>
            <p style={{ margin: "6px 0" }}>Please review each provider&apos;s privacy policy directly:</p>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              <li>OpenAI: <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">openai.com/policies/privacy-policy</a></li>
              <li>Anthropic: <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">anthropic.com/legal/privacy</a></li>
              <li>Google AI: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
            </ul>
          </div>

          <div>
            <strong>Trio&apos;s privacy policy</strong>
            <p style={{ margin: "6px 0 0" }}><a href="https://trioai.co/privacy" target="_blank" rel="noopener noreferrer">trioai.co/privacy</a></p>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Account"
        message="Are you sure you want to delete your account? This action has a 30-day grace period and cannot be undone after that."
        confirmLabel="Delete Account"
        cancelLabel="Cancel"
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteConfirm(false)}
        variant="danger"
      />

      <Modal
        isOpen={showChangePassword}
        onClose={() => {
          setShowChangePassword(false);
          resetChangePasswordForm();
        }}
        title="Change Password"
      >
        <form onSubmit={handleChangePasswordSubmit}>
          <p style={{ color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.5, marginTop: 0, marginBottom: 16 }}>
            You&apos;ll receive 8 new recovery codes. The old ones stop working.
          </p>

          <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--text-secondary)" }}>
            Current Password
          </label>
          <input
            type="password"
            autoComplete="current-password"
            value={cpCurrent}
            onChange={(e) => setCpCurrent(e.target.value)}
            style={{
              width: "100%", padding: "8px 10px", marginBottom: 12,
              background: "var(--bg)", color: "var(--text-primary)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
              fontSize: 13, boxSizing: "border-box",
            }}
          />

          <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--text-secondary)" }}>
            New Password
          </label>
          <input
            type="password"
            autoComplete="new-password"
            value={cpNew}
            onChange={(e) => setCpNew(e.target.value)}
            style={{
              width: "100%", padding: "8px 10px", marginBottom: 12,
              background: "var(--bg)", color: "var(--text-primary)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
              fontSize: 13, boxSizing: "border-box",
            }}
          />

          <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--text-secondary)" }}>
            Confirm New Password
          </label>
          <input
            type="password"
            autoComplete="new-password"
            value={cpConfirm}
            onChange={(e) => setCpConfirm(e.target.value)}
            style={{
              width: "100%", padding: "8px 10px", marginBottom: 8,
              background: "var(--bg)", color: "var(--text-primary)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
              fontSize: 13, boxSizing: "border-box",
            }}
          />

          {(cpNew.length > 0 || cpConfirm.length > 0) && (
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px 0", fontSize: 12 }}>
              <li style={{ color: cpPasswordValid ? "var(--accent-green, #10b981)" : "var(--text-tertiary)" }}>
                {cpPasswordValid ? "✓" : "·"} At least 12 characters
              </li>
              <li style={{ color: cpPasswordsMatch && cpNew.length > 0 ? "var(--accent-green, #10b981)" : "var(--text-tertiary)" }}>
                {cpPasswordsMatch && cpNew.length > 0 ? "✓" : "·"} Passwords match
              </li>
              {cpNew.length > 0 && cpCurrent === cpNew && (
                <li style={{ color: "var(--accent-danger, #ef4444)" }}>
                  · New password must differ from current
                </li>
              )}
            </ul>
          )}

          {cpError && (
            <p style={{ color: "var(--accent-danger, #ef4444)", fontSize: 13, margin: "0 0 12px 0" }}>
              {cpError}
            </p>
          )}

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowChangePassword(false);
                resetChangePasswordForm();
              }}
              disabled={cpSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!cpFormValid || cpSubmitting}
            >
              {cpSubmitting ? "Changing…" : "Change Password"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={showRegenerateConfirm}
        title="Regenerate Recovery Codes?"
        message={
          regenerateError
            ? `Failed: ${regenerateError}`
            : "Your existing 8 recovery codes stop working immediately. You'll receive 8 new codes to save."
        }
        confirmLabel={regenerating ? "Regenerating…" : "Regenerate"}
        cancelLabel="Cancel"
        onConfirm={handleRegenerateCodes}
        onCancel={() => {
          setShowRegenerateConfirm(false);
          setRegenerateError(null);
        }}
        variant="danger"
      />

      {postActionCodes && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "var(--bg)",
            overflowY: "auto",
          }}
        >
          <RecoveryCodesDisplay
            codes={postActionCodes}
            onAcknowledge={() => setPostActionCodes(null)}
          />
        </div>
      )}
    </>
  );
}
